import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { firestore, useCurrentUser } from 'app';
import { format } from 'date-fns';
import { formatInTimeZone, getTimezoneOffset, toZonedTime, fromZonedTime } from 'date-fns-tz';

// Same list the native app's timezone picker offers. Empty value =
// "Phone time" (field cleared → device timezone).
export const TIMEZONE_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Phone time (default)' },
  { value: 'Australia/Brisbane', label: 'Brisbane' },
  { value: 'Australia/Sydney', label: 'Sydney' },
  { value: 'Australia/Melbourne', label: 'Melbourne' },
  { value: 'Australia/Hobart', label: 'Hobart' },
  { value: 'Australia/Adelaide', label: 'Adelaide' },
  { value: 'Australia/Darwin', label: 'Darwin' },
  { value: 'Australia/Perth', label: 'Perth' },
  { value: 'Pacific/Auckland', label: 'Auckland' },
  { value: 'Pacific/Fiji', label: 'Fiji' },
  { value: 'Pacific/Noumea', label: 'Noumea' },
  { value: 'Asia/Makassar', label: 'Bali' },
  { value: 'Asia/Singapore', label: 'Singapore' },
  { value: 'Europe/London', label: 'London' },
  { value: 'America/Los_Angeles', label: 'Los Angeles' },
];

// Current UTC offset label for a zone, e.g. "UTC+10" / "UTC+10:30".
export const tzOffsetLabel = (tz: string): string => {
  if (!tz) return '';
  try {
    const offMin = getTimezoneOffset(tz, new Date()) / 60000;
    const sign = offMin >= 0 ? '+' : '-';
    const abs = Math.abs(offMin);
    const h = Math.floor(abs / 60);
    const m = abs % 60;
    return `UTC${sign}${h}${m ? ':' + String(m).padStart(2, '0') : ''}`;
  } catch {
    return '';
  }
};

// ---- App-wide active zone -------------------------------------------------
// Set once at app start (AppProvider mounts useUserTimezone). All tz-aware
// helpers below read it synchronously so non-hook code can use them.
let activeTz: string | null = null;
export const setActiveUserTimezone = (tz: string | null) => {
  activeTz = tz;
};
export const getActiveUserTimezone = () => activeTz;

// Format in the active zone (device time when no zone saved).
export const ftz = (d: Date | string | number, fmt: string): string =>
  formatInUserTz(d, fmt, activeTz);

// Calendar-date formatter. Date-only strings (YYYY-MM-DD) render as that
// exact calendar day in every timezone; instants format in the active zone.
export const fmtDateOnly = (
  raw: string | number | Date | null | undefined,
  fmt = 'd MMM yyyy',
): string => {
  if (raw === null || raw === undefined || raw === '') return '';
  if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [y, m, d] = raw.split('-').map(Number);
    return format(new Date(y, m - 1, d), fmt);
  }
  return ftz(raw, fmt);
};

// Date whose getHours()/getMinutes()/getDay() return the ACTIVE-ZONE wall
// clock for the given instant — for grid positioning / day math.
export const tzWall = (d: Date | string | number): Date => {
  const dd = d instanceof Date ? d : new Date(d);
  if (!activeTz || isNaN(dd.getTime())) return dd;
  try {
    return toZonedTime(dd, activeTz);
  } catch {
    return dd;
  }
};

// Build the UTC instant for a wall-clock day + "HH:mm" IN THE ACTIVE ZONE.
// `day` is a picker Date whose visible calendar day is the intended day.
export const tzCombine = (day: Date, time: string): Date | null => {
  if (!day || !time) return null;
  const [h, m] = time.split(':').map(Number);
  if (isNaN(h) || isNaN(m)) return null;
  if (!activeTz) {
    const d = new Date(day);
    d.setHours(h, m, 0, 0);
    return d;
  }
  try {
    const dayStr = format(day, 'yyyy-MM-dd');
    return fromZonedTime(`${dayStr}T${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`, activeTz);
  } catch {
    const d = new Date(day);
    d.setHours(h, m, 0, 0);
    return d;
  }
};

// Logged-in user's saved timezone (users/{uid}.timezone). null = phone time.
export function useUserTimezone(): string | null {
  const { user } = useCurrentUser();
  // Seed from the module-level zone so pages mounted after the app resolved
  // the saved timezone format correctly on their very first render.
  const [tz, setTz] = useState<string | null>(() => getActiveUserTimezone());
  useEffect(() => {
    let active = true;
    if (!user?.uid) return;
    getDoc(doc(firestore, 'users', user.uid))
      .then((snap) => {
        if (!active) return;
        const v = (snap.data() as any)?.timezone;
        const resolved = typeof v === 'string' && v ? v : null;
        setActiveUserTimezone(resolved);
        setTz(resolved);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [user?.uid]);
  return tz;
}

// Format a date in the user's saved zone; falls back to device time.
export const formatInUserTz = (
  d: Date | string | number,
  fmt: string,
  tz?: string | null,
): string => {
  const dd = d instanceof Date ? d : new Date(d);
  if (isNaN(dd.getTime())) return '--';
  try {
    return tz ? formatInTimeZone(dd, tz, fmt) : format(dd, fmt);
  } catch {
    return format(dd, fmt);
  }
};
